import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Code2, Globe, HelpCircle, MoreHorizontal, Send } from "lucide-react";
import { Badge, Button, DropdownMenu, Skeleton, Tooltip } from "@/components/ui";
import { HoloBadge } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { ActorRef, DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { CableStatus } from "@/utils/actioncable";
import { URLS } from "@/utils/routes";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { PromptStudioScopeContext } from "../PromptStudioScope";
import { DisabledReason, inFlightReasonFor, READ_ONLY_REASON } from "./DisabledReason";
import { EditableName } from "./EditableName";
import { PresenceAvatars } from "./PresenceAvatars";
import { PromptStudioHeader } from "./PromptStudioHeader";
import { StatusPill } from "./StatusPill";
import { useViewer } from "../viewer";
import { PromptStudioBackState } from "../backLink";

export type EditorMode = "owner" | "readOnly" | "globalForCoach" | "archived";

export interface EditorHeaderProps {
  /** Undefined while loading → skeleton header. */
  pkg?: DesignPackageDetail;
  /** Renders the "Reconnecting…" badge once a connected socket drops, or when it is refused. */
  connection: CableStatus;
  /** owner: full controls; readOnly: assistant READ; globalForCoach: a coach viewing a published global; archived: Restore only. */
  mode: EditorMode;
  viewers?: ActorRef[];
  /** Disables Publish/Unpublish and shows who is working. */
  inFlight?: DesignPackageDetail["inFlight"];
  onOpenHelp(topic: "how" | "ask" | "backgrounds"): void;
  /** Inline rename; the name is static text when absent. */
  onRename?(name: string): Promise<void>;
  onPublish?(): void;
  onUnpublish?(): void;
  onDuplicate?(): void;
  onArchive?(): void;
  onRestore?(): void;
  /** Shown only when provided: the viewer can post for players and the design is ready to post. */
  onPostForPlayer?(): void;
  /** Admin-only Trace sheet; shown in every package state so a stuck create or refine can be inspected. */
  onOpenTrace?(): void;
  /** Admin-only Source sheet (a slide's template code); the page passes it only when not impersonating. */
  onOpenSource?(): void;
}

export const EditorHeader: React.FC<EditorHeaderProps> = ({
  pkg,
  connection,
  mode,
  viewers,
  inFlight,
  onOpenHelp,
  onRename,
  onPublish,
  onUnpublish,
  onDuplicate,
  onArchive,
  onRestore,
  onPostForPlayer,
  onOpenTrace,
  onOpenSource,
}) => {
  const adminScope = React.useContext(PromptStudioScopeContext);
  const history = useHistory();
  const location = useLocation<({ justDuplicated?: boolean } & PromptStudioBackState) | undefined>();
  // Set by the duplicate flow so the copy opens with "(Copy)" selected in the name.
  const justDuplicated = !!location.state?.justDuplicated;
  const { isAdmin } = useSocialCoachSession();
  const viewer = useViewer();
  // The first connect on page load isn't a reconnect; only a dropped or refused socket shows the badge.
  const [hasConnected, setHasConnected] = React.useState(connection === "connected");
  React.useEffect(() => {
    if (connection === "connected") setHasConnected(true);
  }, [connection]);
  const reconnecting = connection === "rejected" || (hasConnected && connection !== "connected");
  // Opened from a prompt page, the back link returns there; otherwise it goes to the design list.
  const cameFrom = location.state?.back;
  const backTo = cameFrom?.to ?? (adminScope ? URLS.admin.promptStudio : URLS.coach.promptStudio);
  const backLabel = cameFrom?.label;

  const readOnly = mode === "readOnly";
  const inFlightReason = inFlightReasonFor(inFlight, viewer);
  // Read-only wins: waiting wouldn't let this viewer act anyway.
  const lockReason = readOnly ? READ_ONLY_REASON : inFlightReason;

  // There is nothing to post until the design is generated, globals are never posted directly, and
  // super admins don't post for players (they impersonate the account instead).
  const showPostForPlayer =
    !!pkg &&
    !!onPostForPlayer &&
    !isAdmin &&
    mode === "owner" &&
    !pkg.isGlobal &&
    !pkg.archived &&
    pkg.status === "completed";
  const showTrace = isAdmin && !!pkg && !!onOpenTrace;
  const showSource = isAdmin && !!pkg && !!onOpenSource;
  const postForPlayerReason = inFlight ?? pkg?.inFlight ? "Wait for the current change to finish." : null;

  const openPrompt = () => {
    if (pkg?.promptId) history.push(URLS.coach.prompt.replace(":promptId", String(pkg.promptId)));
  };

  const primaryAction = () => {
    if (!pkg) return null;
    switch (mode) {
      case "owner":
      case "readOnly":
        if (!pkg.publishedAt && onPublish)
          return (
            <DisabledReason reason={lockReason} data-testid="publish-wrapper">
              <Button variant="default" disabled={!!lockReason} onClick={onPublish}>
                Publish
              </Button>
            </DisabledReason>
          );
        if (pkg.publishedAt && onUnpublish)
          return (
            <DisabledReason reason={lockReason} data-testid="unpublish-wrapper">
              <Button variant="outline" disabled={!!lockReason} onClick={onUnpublish}>
                Unpublish
              </Button>
            </DisabledReason>
          );
        return null;
      case "globalForCoach":
        return onDuplicate ? (
          <Button variant="default" onClick={onDuplicate}>
            Add to my designs
          </Button>
        ) : null;
      case "archived":
        return onRestore ? (
          <Button variant="default" onClick={onRestore}>
            Restore
          </Button>
        ) : null;
    }
  };

  // Items that stay in the overflow at every width; the help and Post for a player items only
  // move in below lg, where their inline buttons are hidden.
  const overflowItems: React.ReactNode[] = [];
  if (pkg && mode === "owner" && onDuplicate)
    overflowItems.push(<DropdownMenu.Item key="duplicate" label="Duplicate" onClick={onDuplicate} />);
  if (pkg?.promptId && (mode === "owner" || readOnly))
    overflowItems.push(<DropdownMenu.Item key="open-prompt" label="Open prompt" onClick={openPrompt} />);
  if (pkg && mode === "owner" && onArchive)
    overflowItems.push(
      <DropdownMenu.Item
        key="archive"
        label="Archive"
        variant="destructive"
        disabled={!!inFlightReason}
        onClick={onArchive}
      />
    );
  if (pkg && mode === "archived" && onDuplicate)
    overflowItems.push(<DropdownMenu.Item key="duplicate" label="Duplicate" onClick={onDuplicate} />);

  return (
    <PromptStudioHeader
      backTo={backTo}
      backLabel={backLabel}
      testId="editor-header"
      actions={
        <div className="flex items-center gap-2">
          <div className="hidden lg:flex">
            <DropdownMenu>
              <Tooltip>
                <Tooltip.Trigger render={<span className="inline-flex" />}>
                  <DropdownMenu.Trigger render={<Button variant="ghost" size="icon" aria-label="Help" />}>
                    <HelpCircle aria-hidden="true" />
                  </DropdownMenu.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Content>Help</Tooltip.Content>
              </Tooltip>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item label="How Prompt Studio works" onClick={() => onOpenHelp("how")} />
                <DropdownMenu.Item label="What can I ask for?" onClick={() => onOpenHelp("ask")} />
                <DropdownMenu.Item label="How backgrounds work" onClick={() => onOpenHelp("backgrounds")} />
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>

          {showSource && (
            <div className="hidden lg:flex">
              <Button variant="ghost" size="sm" onClick={onOpenSource}>
                <Code2 aria-hidden="true" />
                Source
              </Button>
            </div>
          )}

          {showTrace && (
            <div className="hidden lg:flex">
              <Button variant="ghost" size="sm" onClick={onOpenTrace}>
                Trace
              </Button>
            </div>
          )}

          {showPostForPlayer && (
            <div className="hidden lg:flex">
              <DisabledReason reason={postForPlayerReason} data-testid="post-for-player-wrapper">
                <Button variant="outline" disabled={!!postForPlayerReason} onClick={onPostForPlayer}>
                  <Send />
                  Post for a player
                </Button>
              </DisabledReason>
            </div>
          )}

          {primaryAction()}

          <div className={cn("flex", overflowItems.length === 0 && "lg:hidden")}>
            <DropdownMenu>
              <DropdownMenu.Trigger render={<Button variant="ghost" size="icon" aria-label="More actions" />}>
                <MoreHorizontal aria-hidden="true" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {overflowItems}
                <DropdownMenu.Item
                  className="lg:hidden"
                  label="How Prompt Studio works"
                  onClick={() => onOpenHelp("how")}
                />
                <DropdownMenu.Item
                  className="lg:hidden"
                  label="What can I ask for?"
                  onClick={() => onOpenHelp("ask")}
                />
                <DropdownMenu.Item
                  className="lg:hidden"
                  label="How backgrounds work"
                  onClick={() => onOpenHelp("backgrounds")}
                />
                {showSource && <DropdownMenu.Item className="lg:hidden" label="Source" onClick={onOpenSource} />}
                {showTrace && <DropdownMenu.Item className="lg:hidden" label="Trace" onClick={onOpenTrace} />}
                {showPostForPlayer && (
                  <DropdownMenu.Item
                    className="lg:hidden"
                    label="Post for a player"
                    disabled={!!postForPlayerReason}
                    onClick={onPostForPlayer}
                  />
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
        </div>
      }
    >
      {pkg ? (
        onRename ? (
          <EditableName
            key={pkg.id}
            value={pkg.name}
            onSave={onRename}
            disabled={mode !== "owner"}
            autoFocusSelect={justDuplicated}
          />
        ) : (
          <div className="-my-1 truncate py-1 text-xl leading-7 font-bold text-slate-800">{pkg.name}</div>
        )
      ) : (
        <Skeleton className="h-7 w-48" data-testid="editor-header-skeleton" />
      )}
      {pkg && <StatusPill pkg={{ ...pkg, inFlight }} size="medium" />}
      {pkg?.isGlobal && <HoloBadge label="Global" size="md" icon={Globe} />}
      {viewers && <PresenceAvatars viewers={viewers} />}
      {reconnecting && <Badge variant="outline">Reconnecting…</Badge>}
    </PromptStudioHeader>
  );
};
